import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, Copy, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

const PLAN_LABEL = {
  parentia_solo_monthly: "Solo · 1 mois",
  parentia_solo_yearly: "Solo · 12 mois",
  parentia_famille_monthly: "Famille+ · 1 mois",
};

export default function GiftsPage() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    const { data } = await api.get("/gifts/mine");
    setSent(data);
  };
  useEffect(() => { load(); }, []);

  const redeem = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code: code.trim().toUpperCase() });
      toast.success(`Cadeau utilisé ! +${data.days_added} jours débloqués 💛`);
      setCode("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'utiliser ce code");
    }
    setBusy(false);
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    setCopied(c);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Code copié 📋");
  };

  return (
    <div className="space-y-8" data-testid="gifts-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-[#FBE2E9] grid place-items-center">
          <Gift className="h-6 w-6 text-[#E8899F]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Cadeaux
          </h1>
          <p className="text-[#77849C] text-sm">
            Utilise un code reçu ou retrouve les cadeaux que tu as offerts.
          </p>
        </div>
      </div>

      <section className="soft-card p-6">
        <h2 className="text-xl font-semibold text-[#16233D]">Utiliser un code cadeau</h2>
        <form onSubmit={redeem} className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label htmlFor="gift-code" className="sr-only">Code cadeau</Label>
            <Input
              id="gift-code"
              data-testid="redeem-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="KYN-XXXXXXXX"
              required
            />
          </div>
          <Button data-testid="redeem-btn" type="submit" disabled={busy || !code} className="brand-btn rounded-full px-6">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Utilisation...</> : "Utiliser"}
          </Button>
        </form>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#16233D] mb-4">Cadeaux offerts</h2>
        {sent.length === 0 && (
          <p className="text-[#77849C]">Tu n'as pas encore offert de cadeau. Rends-toi sur la page Abonnement pour en créer un 🎁</p>
        )}
        <div className="space-y-3">
          {sent.map((g) => {
            const paid = g.payment_status === "paid";
            const deliverable = g.deliverable_now;
            return (
              <article key={g.id} data-testid={`gift-${g.id}`} className="soft-card p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-semibold text-[#16233D]">
                      {PLAN_LABEL[g.lookup_key]} pour {g.recipient_name}
                    </p>
                    <p className="text-sm text-[#44506B]">{g.recipient_email}</p>
                    {g.message && (
                      <p className="mt-2 text-sm italic text-[#16233D] bg-[#EDF2F9] rounded-xl px-3 py-2">
                        « {g.message} »
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      <span className={`rounded-full px-3 py-0.5 font-semibold ${paid ? "bg-[#D2EDE1] text-[#16233D]" : "bg-[#FBE2E9] text-[#E8899F]"}`}>
                        {paid ? "Payé" : "En attente"}
                      </span>
                      {g.redeemed ? (
                        <span className="rounded-full bg-[#E3EAF7] text-[#16233D] px-3 py-0.5 font-semibold">
                          Utilisé 💛
                        </span>
                      ) : (
                        paid && (
                          <span className={`rounded-full px-3 py-0.5 font-semibold ${deliverable ? "bg-[#FFF4DC] text-[#16233D]" : "bg-white border border-[#E3E8F1] text-[#77849C]"}`}>
                            {deliverable
                              ? "Prêt à partager"
                              : `Livraison le ${new Date(g.deliver_at).toLocaleDateString("fr-FR")}`}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  {paid && !g.redeemed && (
                    <div className="text-right">
                      <p className="text-xs font-bold uppercase tracking-widest text-[#77849C]">Code</p>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-sm sm:text-base text-[#16233D]">{g.code}</code>
                        <button
                          data-testid={`copy-gift-${g.id}`}
                          onClick={() => copyCode(g.code)}
                          className="rounded-full bg-[#EDF2F9] hover:bg-[#E3E8F1] p-1.5 transition"
                          aria-label="Copier le code"
                        >
                          {copied === g.code ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="soft-card p-6 !bg-[#FFF4DC]">
        <div className="flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-[#E8899F] shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-[#16233D]">Comment offrir un abonnement ?</p>
            <ol className="mt-2 list-decimal list-inside text-sm text-[#44506B] space-y-1">
              <li>Va sur la page <span className="font-semibold">Abonnement</span></li>
              <li>Clique sur "Offrir" sur la formule de ton choix</li>
              <li>Renseigne le destinataire + une date de livraison (fête des mères, Noël…)</li>
              <li>Paie une fois — pas d'abonnement récurrent</li>
              <li>Partage le code à la date choisie 💛</li>
            </ol>
          </div>
        </div>
      </section>
    </div>
  );
}
